import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  type ExpandedState,
} from '@tanstack/react-table';
import { ExternalLink, Search } from 'lucide-react';
import { detailNumber, detailText, fetchProductVendors, fetchProducts, nextSort } from '../api/products';
import type { Product, ProductFilters, ProductSort, ProductSortKey } from '../api/products';
import type { VendorOption } from '../api/home_value_checks';
import ExportButton from '../components/ExportButton';
import { HeaderActions } from '../components/HeaderActions';
import { LabelBadge } from '../components/badges';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { FilterSelect } from '../components/ui/filter-select';
import { MultiSelect } from '../components/ui/multi-select';
import { Tooltip } from '../components/ui/tooltip';
import { DataTable } from '../components/data-table/DataTable';
import {
  ACTIONS_COL_WIDTH_COMPACT,
  ExpandedFields,
  ExpandedPanel,
  ExpandedSection,
  NsIdLink,
  expandColumn,
} from '../components/data-table/columns';
import { RowActionLink, RowActionMenu, RowActions, type RowMenuItem } from '../components/data-table/RowActions';
import { SortableHeader } from '../components/data-table/SortableHeader';
import { DealerStockCell, VendorStockCell } from '../components/data-table/productStockColumns';
import {
  ColumnsMenu,
  FilterBar,
  FilterField,
  FilterRow,
  PageHeading,
  ResetFiltersButton,
  TablePagination,
} from '../components/data-table/TableChrome';
import { MAGENTO_BASE_URL, NS_BASE_URL } from '../config/external_links';
import { fmtPrice } from '../utils/format';
import { useItemDetail } from '../hooks/useItemDetail';

const PAGE_SIZES = [50, 100, 250, 500, 1000] as const;

const STATUS_OPTIONS = [
  { value: 'active',   label: 'Active',   dot: 'bg-green-500' },
  { value: 'inactive', label: 'Inactive', dot: 'bg-zinc-400'  },
  { value: 'all',      label: 'All' },
];

// BOT-505 — same categories as Home Value's vendor stock filter.
const VENDOR_STOCK_OPTIONS = [
  { value: '',         label: 'All' },
  { value: 'in_stock', label: 'In stock' },
  { value: 'zero',     label: 'Zero' },
  { value: 'no_data',  label: 'No reading' },
];
type VendorStockFilter = NonNullable<ProductFilters['vendor_stock']>;

/** The sort applied to the list, if any (the API defaults `dir` to desc). */
function sortOf(f: ProductFilters): ProductSort | null {
  return f.sort ? { key: f.sort, dir: f.dir ?? 'desc' } : null;
}

const VENDOR_STOCK_MIN_TOOLTIP = 'Counts units only — International, Direct ship and No PDC stock items have no number';
const OTHER_VENDOR = 'Other vendor';

export default function ProductsPage() {
  const [filters, setFilters] = useState<ProductFilters>({ page: 1, per_page: 50, status: 'active' });
  // The catalogue is large, so text filters apply on submit rather than on every
  // keystroke — one request per search instead of one per character.
  const [draftSearch, setDraftSearch]             = useState('');
  const [draftVendors, setDraftVendors]           = useState<string[]>([]);
  const [draftManufacturer, setDraftManufacturer] = useState('');
  const [draftPriceMin, setDraftPriceMin]         = useState('');
  const [draftPriceMax, setDraftPriceMax]         = useState('');
  const [draftOnHandMin, setDraftOnHandMin]       = useState('');
  const [draftStatus, setDraftStatus]             = useState<'active' | 'inactive' | 'all'>('active');
  const [draftVendorStock, setDraftVendorStock]   = useState<VendorStockFilter | ''>('');
  const [draftVendorStockMin, setDraftVendorStockMin] = useState('');
  const [vendorOptions, setVendorOptions]         = useState<VendorOption[]>([]);

  const [items, setItems]     = useState<Product[]>([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

  const { detailFor, loadDetail } = useItemDetail();

  // Two quick sort clicks send two 1-3 s requests; only the latest may land,
  // or the table would show one order under the other's arrow.
  const requestSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    setExpanded({});
    try {
      const data = await fetchProducts(filters);
      if (seq !== requestSeq.current) return;
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      if (seq !== requestSeq.current) return;
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [filters]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    fetchProductVendors().then(setVendorOptions).catch(() => setVendorOptions([]));
  }, []);

  // Client names only — the code never reaches the screen (decision vendorlabels).
  const vendorName = useCallback(
    (code: string | null) => {
      if (!code || vendorOptions.length === 0) return null;
      return vendorOptions.find(v => v.code === code)?.name ?? OTHER_VENDOR;
    },
    [vendorOptions],
  );

  // A header click sorts at once (server-side) and returns to page 1.
  const sort = sortOf(filters);
  const toggleSort = useCallback((key: ProductSortKey) => {
    setFilters(f => {
      const next = nextSort(sortOf(f), key);
      return { ...f, sort: next?.key, dir: next?.dir, page: 1 };
    });
  }, []);

  function applyFilters(e: React.FormEvent) {
    e.preventDefault();
    const priceMin  = parseFloat(draftPriceMin);
    const priceMax  = parseFloat(draftPriceMax);
    const onHandMin = parseInt(draftOnHandMin, 10);
    const stockMin  = parseInt(draftVendorStockMin, 10);
    setFilters(f => ({
      search:             draftSearch.trim() || undefined,
      vendor:             draftVendors.length ? draftVendors : undefined,
      manufacturer:       draftManufacturer.trim() || undefined,
      purchase_price_min: isNaN(priceMin)  ? undefined : priceMin,
      purchase_price_max: isNaN(priceMax)  ? undefined : priceMax,
      on_hand_min:        isNaN(onHandMin) ? undefined : onHandMin,
      status:             draftStatus,
      vendor_stock:       draftVendorStock || undefined,
      vendor_stock_min:   isNaN(stockMin) || stockMin < 0 ? undefined : stockMin,
      // Search narrows the list; the operator's sort stays.
      sort: f.sort,
      dir:  f.dir,
      page: 1,
      per_page: f.per_page ?? 50,
    }));
  }

  function resetFilters() {
    setDraftSearch('');
    setDraftVendors([]);
    setDraftManufacturer('');
    setDraftPriceMin('');
    setDraftPriceMax('');
    setDraftOnHandMin('');
    setDraftStatus('active');
    setDraftVendorStock('');
    setDraftVendorStockMin('');
    // Reset clears the sort too: back to NS Item ID order.
    setFilters(f => ({ page: 1, per_page: f.per_page ?? 50, status: 'active' }));
  }

  const hasActiveFilters =
    !!draftSearch || draftVendors.length > 0 || !!draftManufacturer ||
    !!draftPriceMin || !!draftPriceMax || !!draftOnHandMin || draftStatus !== 'active' ||
    !!draftVendorStock || !!draftVendorStockMin || !!filters.sort;

  const columns = useMemo<ColumnDef<Product>[]>(() => [
    expandColumn<Product>(),
    {
      // SKU leads here as it does everywhere else — this view used to start
      // with the NS ID, which broke the scan pattern between tables (BOT-392).
      id: 'sku',
      size: 230,
      enableHiding: false,
      meta: { label: 'SKU' },
      header: 'SKU',
      cell: ({ row }) => (
        <>
          <div className="whitespace-nowrap font-mono text-[12.5px] font-medium">{row.original.sku ?? '—'}</div>
          <div className="max-w-[240px] truncate text-xs text-muted-foreground" title={row.original.name ?? ''}>
            {row.original.name ?? ''}
          </div>
        </>
      ),
    },
    {
      id: 'ns',
      meta: { label: 'NS ID' },
      header: 'NS ID',
      cell: ({ row }) => <NsIdLink id={row.original.ns_product_id} />,
    },
    {
      id: 'vendor',
      meta: { label: 'Vendor' },
      header: 'Vendor',
      cell: ({ row }) =>
        row.original.vendor ? (
          <span className="whitespace-nowrap text-zinc-700">{vendorName(row.original.vendor)}</span>
        ) : (
          <span className="text-zinc-300">—</span>
        ),
    },
    {
      id: 'manufacturer',
      meta: { label: 'Manufacturer' },
      header: 'Manufacturer',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-zinc-700">{row.original.manufacturer ?? '—'}</span>
      ),
    },
    {
      id: 'purchase',
      meta: { label: 'Purchase cost', align: 'right' },
      header: 'Purchase cost',
      cell: ({ row }) => (
        <div className="whitespace-nowrap">
          <div className="font-medium">{fmtPrice(row.original.purchase_price)}</div>
          {row.original.core_charge != null && (
            <div className="text-[11.5px] text-zinc-400">{fmtPrice(row.original.core_charge)} core</div>
          )}
        </div>
      ),
    },
    {
      id: 'website',
      meta: { label: 'Website price', align: 'right' },
      header: 'Website price',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">{fmtPrice(row.original.website_price)}</span>
      ),
    },
    {
      id: 'onHand',
      meta: { label: 'On hand', align: 'right' },
      header: () => (
        <SortableHeader label="On hand" dir={sort?.key === 'on_hand' ? sort.dir : null} onClick={() => toggleSort('on_hand')} />
      ),
      cell: ({ row }) => (
        <span className={row.original.on_hand > 0 ? 'text-foreground' : 'text-zinc-400'}>
          {row.original.on_hand}
        </span>
      ),
    },
    {
      id: 'vendorStock',
      meta: { label: 'Vendor stock', align: 'right' },
      header: () => (
        <SortableHeader
          label="Vendor stock"
          dir={sort?.key === 'vendor_stock' ? sort.dir : null}
          onClick={() => toggleSort('vendor_stock')}
        />
      ),
      cell: ({ row }) => <VendorStockCell product={row.original} />,
    },
    {
      id: 'dealerStock',
      meta: { label: 'Dealer', align: 'right' },
      header: () => (
        <SortableHeader
          label="Dealer"
          dir={sort?.key === 'dealer_stock' ? sort.dir : null}
          onClick={() => toggleSort('dealer_stock')}
        />
      ),
      cell: ({ row }) => <DealerStockCell product={row.original} />,
    },
    {
      id: 'status',
      meta: { label: 'Status' },
      header: 'Status',
      cell: ({ row }) => (
        <div className="flex flex-wrap items-center gap-1">
          {row.original.inactive && <Badge variant="neutral" dot="bg-zinc-400">Inactive</Badge>}
          {row.original.item_status ? (
            <LabelBadge value={row.original.item_status} />
          ) : (
            !row.original.inactive && <span className="text-zinc-300">—</span>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      // Two controls, not three — the compact width, per the review.
      size: ACTIONS_COL_WIDTH_COMPACT,
      enableHiding: false,
      meta: { label: 'Actions', align: 'right', padding: 'tight' },
      header: 'Actions',
      cell: ({ row }) => {
        const p = row.original;
        // Details is the action this table exists for, so it is a button rather
        // than a menu entry — one click instead of two.
        const menuItems: RowMenuItem[] = [
          { label: 'Open in NetSuite', icon: ExternalLink, href: `${NS_BASE_URL}${p.ns_product_id}` },
        ];
        if (p.magento_id != null) {
          menuItems.push({
            label: 'Open in Magento',
            icon: ExternalLink,
            href: `${MAGENTO_BASE_URL}${p.magento_id}`,
          });
        }
        return (
          <RowActions>
            <RowActionLink label="Details" icon={Search} href={`/all-items/${p.ns_product_id}?from=all`} />
            <RowActionMenu items={menuItems} />
          </RowActions>
        );
      },
    },
  ], [vendorName, sort?.key, sort?.dir, toggleSort]);

  const table = useReactTable({
    data: items,
    columns,
    getRowId: row => String(row.ns_product_id),
    state: { expanded, columnVisibility },
    onExpandedChange: setExpanded,
    onColumnVisibilityChange: setColumnVisibility,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    initialState: { columnPinning: { left: ['expand', 'sku'], right: ['actions'] } },
  });

  useEffect(() => {
    if (expanded === true) return;
    Object.keys(expanded).filter(id => expanded[id]).forEach(id => void loadDetail(id));
  }, [expanded, loadDetail]);

  const perPage     = filters.per_page ?? 50;
  const currentPage = filters.page ?? 1;
  const totalPages  = Math.max(1, Math.ceil(total / perPage));

  return (
    <>
      <HeaderActions>
        <ExportButton resource="all_items" filters={{ ...filters, page: undefined, per_page: undefined }} />
      </HeaderActions>

      <PageHeading title="All Items" count={total} />

      <form onSubmit={applyFilters}>
        <FilterBar>
          <FilterRow className="gap-3">
            <FilterField label="SKU / name">
              <Input
                type="text"
                placeholder="Search…"
                className="w-56"
                value={draftSearch}
                onChange={e => setDraftSearch(e.target.value)}
              />
            </FilterField>
            <MultiSelect
              label="Vendor"
              options={vendorOptions.map(v => ({ value: v.code, label: v.name }))}
              selected={draftVendors}
              onChange={setDraftVendors}
            />
            <FilterField label="Manufacturer">
              <Input
                type="text"
                placeholder="e.g. CAT"
                className="w-32"
                value={draftManufacturer}
                onChange={e => setDraftManufacturer(e.target.value)}
              />
            </FilterField>
            <FilterSelect
              label="Status"
              options={STATUS_OPTIONS}
              value={draftStatus}
              onChange={v => setDraftStatus(v as 'active' | 'inactive' | 'all')}
            />
          </FilterRow>

          <FilterRow className="gap-4">
            <FilterField label="Purchase $">
              <Input
                type="number"
                placeholder="min"
                min={0}
                step={0.01}
                className="w-[68px]"
                value={draftPriceMin}
                onChange={e => setDraftPriceMin(e.target.value)}
              />
              <span className="text-zinc-300">–</span>
              <Input
                type="number"
                placeholder="max"
                min={0}
                step={0.01}
                className="w-[68px]"
                value={draftPriceMax}
                onChange={e => setDraftPriceMax(e.target.value)}
              />
            </FilterField>

            <FilterField label="On hand ≥">
              <Input
                type="number"
                placeholder="0"
                min={0}
                step={1}
                className="w-[68px]"
                value={draftOnHandMin}
                onChange={e => setDraftOnHandMin(e.target.value)}
              />
            </FilterField>

            <FilterSelect
              label="Vendor stock"
              options={VENDOR_STOCK_OPTIONS}
              value={draftVendorStock}
              onChange={v => setDraftVendorStock(v as VendorStockFilter | '')}
            />

            <FilterField label="Vendor stock ≥">
              <Tooltip label={VENDOR_STOCK_MIN_TOOLTIP}>
                <Input
                  type="number"
                  aria-label="Vendor stock ≥"
                  placeholder="0"
                  min={0}
                  step={1}
                  className="w-[68px]"
                  value={draftVendorStockMin}
                  onChange={e => setDraftVendorStockMin(e.target.value)}
                />
              </Tooltip>
            </FilterField>

            <Button type="submit">
              <Search className="h-3.5 w-3.5" />
              Search
            </Button>

            <div className="ml-auto flex items-center gap-2">
              {hasActiveFilters && <ResetFiltersButton onClick={resetFilters} />}
              <ColumnsMenu table={table} />
            </div>
          </FilterRow>
        </FilterBar>
      </form>

      <DataTable
        table={table}
        loading={loading}
        error={error}
        emptyMessage="No products found"
        renderExpanded={row => {
          const detail = detailFor(row.original.ns_product_id);
          if (detail.loading) {
            return <div className="py-4 pl-[84px] text-[12.5px] text-muted-foreground">Loading details…</div>;
          }
          if (detail.error) {
            return <div className="py-4 pl-[84px] text-[12.5px] text-red-600">{detail.error}</div>;
          }
          return (
            <ExpandedPanel>
              <ExpandedSection title="Item">
                <ExpandedFields
                  rows={[
                    ['Display name', detailText(detail.data, 'display_name') ?? '—'],
                    ['Old part number', detailText(detail.data, 'old_part_number') ?? '—'],
                    ['Type', detailText(detail.data, 'product_type') ?? '—'],
                    ['Country', detailText(detail.data, 'manufacturer_country') ?? '—'],
                    ['Warranty', detailText(detail.data, 'warranty') ?? '—'],
                  ]}
                />
              </ExpandedSection>

              <ExpandedSection title="Pricing & stock">
                <ExpandedFields
                  rows={[
                    ['Special price', fmtPrice(detailNumber(detail.data, 'special_price'))],
                    ['Core charge', fmtPrice(row.original.core_charge)],
                    ['Website visible', detailText(detail.data, 'website_visible') ?? '—'],
                    ['Returnable', detailText(detail.data, 'returnable_status') ?? '—'],
                    ['Lead time', detailText(detail.data, 'home_value') ?? '—'],
                  ]}
                />
              </ExpandedSection>

              <ExpandedSection title="Dimensions">
                <ExpandedFields
                  rows={[
                    ['Weight', detailText(detail.data, 'weight') ?? '—'],
                    ['Height', detailText(detail.data, 'height') ?? '—'],
                    ['Width', detailText(detail.data, 'width') ?? '—'],
                    ['Length', detailText(detail.data, 'length') ?? '—'],
                  ]}
                />
              </ExpandedSection>
            </ExpandedPanel>
          );
        }}
      />

      <TablePagination
        page={currentPage}
        totalPages={totalPages}
        total={total}
        perPage={perPage}
        pageSizes={PAGE_SIZES}
        onPageChange={p => setFilters(f => ({ ...f, page: p }))}
        onPerPageChange={n => setFilters(f => ({ ...f, per_page: n, page: 1 }))}
      />
    </>
  );
}
